import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Star } from "lucide-react";
import { toast } from "sonner";
import { clientApi } from "../lib/api";

/**
 * NPS response modal. Fetches list of open surveys on mount, cycles through them one at a time.
 */
export default function NPSModal({ surveys, onClose, onDone }) {
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const current = surveys[idx];
  if (!current) { onClose(); return null; }

  const submit = async () => {
    if (score == null) { toast.error("Escolha uma nota."); return; }
    setBusy(true);
    try {
      await clientApi.post(`/nps/${current.id}/respond`, { score, comment });
      toast.success("Obrigado pela sua resposta!");
      setScore(null); setComment("");
      if (idx + 1 < surveys.length) {
        setIdx(idx + 1);
      } else {
        onDone && onDone();
        onClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar.");
    } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="nps-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 30, scale: 0.95 }} animate={{ y: 0, scale: 1 }} className="relative bg-[#141414] border border-white/10 rounded-2xl max-w-lg w-full p-6 md:p-8">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/5 rounded-full"><X className="w-4 h-4" /></button>
        <div className="text-xs uppercase tracking-widest text-accent-orange mb-1 flex items-center gap-2">
          <Star className="w-3 h-3" /> Pesquisa NPS {surveys.length > 1 && <span className="text-neutral-500 lowercase">({idx + 1}/{surveys.length})</span>}
        </div>
        <div className="font-display text-2xl mb-2">{current.title || "Em uma escala de 0 a 10, quanto você recomendaria a Places?"}</div>
        <p className="text-xs text-neutral-500 mb-5">{current.subtitle || "Sua resposta leva menos de 30 segundos e nos ajuda muito."}</p>
        <div className="grid grid-cols-11 gap-1.5 mb-2">
          {Array.from({ length: 11 }).map((_, i) => (
            <button key={i} onClick={() => setScore(i)} data-testid={`nps-score-${i}`}
              className={`aspect-square rounded-lg font-bold text-sm border-2 transition ${score === i ? "bg-accent-orange border-accent-orange text-black scale-110" : "border-white/10 hover:border-accent-orange text-neutral-300"}`}>
              {i}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-neutral-500 uppercase tracking-widest mb-5">
          <span>Nada provável</span><span>Muito provável</span>
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Deixe um comentário (opcional)" data-testid="nps-comment" className="w-full bg-black border border-white/10 rounded-lg px-3 py-2.5 text-sm mb-4" />
        <button onClick={submit} disabled={score == null || busy} data-testid="nps-submit" className="w-full bg-accent-orange hover:bg-accent-yellow hover:text-black text-white font-bold py-3 rounded-full text-sm disabled:opacity-50">
          {busy ? "Enviando…" : (idx + 1 < surveys.length ? "Enviar e ir para próxima" : "Enviar resposta")}
        </button>
      </motion.div>
    </motion.div>
  );
}
